import { SearchDocument, SearchResult } from '@backstage/plugin-search-common';

export type ResultMetadata = {
  sourceLabel: string; // always set; 'Catalog' | 'Docs' | raw type string
  kind?: string; // e.g. 'Component', 'Template'
  lifecycle?: string; // e.g. 'production', 'experimental'
  owner?: string; // e.g. 'group:default/devex'
  entityName?: string; // TechDocs only: entity the page belongs to (doc.name)
  pageTitle?: string; // TechDocs only: page title (doc.title)
};

// Narrow cast for optional indexed fields that catalog and techdocs collators
// write into the document but that SearchDocument doesn't type explicitly.
// We read these defensively rather than importing backend packages.
type IndexedFields = Partial<{
  kind: string;
  lifecycle: string;
  owner: string;
  name: string;
  componentType: string;
}>;

function presentString(value: string | undefined): string | undefined {
  // Treat absent and whitespace-only values the same: both are "missing".
  return value?.trim() || undefined;
}

function sourceLabel(type: string): string {
  switch (type) {
    case 'software-catalog':
      return 'Catalog';
    case 'techdocs':
      return 'Docs';
    case 'quick-link':
      return 'Quick Link';
    default:
      // Unknown index types still get a chip so the result row isn't chipless.
      return type;
  }
}

export function extractResultMetadata(result: SearchResult): ResultMetadata {
  const fields = result.document as SearchDocument & IndexedFields;

  const metadata: ResultMetadata = {
    sourceLabel: sourceLabel(result.type),
  };

  const kind = presentString(fields.kind);
  if (kind !== undefined) metadata.kind = kind;

  const lifecycle = presentString(fields.lifecycle);
  if (lifecycle !== undefined) metadata.lifecycle = lifecycle;

  const owner = presentString(fields.owner);
  if (owner !== undefined) metadata.owner = owner;

  // TechDocs results carry the entity name as `name` and the page title as
  // `title`. Catalog results also have `title` (entity title) but no `name`
  // field in the same sense — only surface entityName for techdocs results so
  // the chip doesn't duplicate what the title line already shows.
  if (result.type === 'techdocs') {
    const entityName = presentString(fields.name);
    if (entityName !== undefined) metadata.entityName = entityName;

    const pageTitle = presentString(result.document.title);
    if (pageTitle !== undefined) metadata.pageTitle = pageTitle;
  }

  return metadata;
}
